import React, { useEffect, useRef, useState } from "react";

/**
 * Animated counter that starts counting when in viewport.
 * Props:
 *   value: target number
 *   duration: ms
 *   prefix / suffix: string
 *   format: "currency-brl" | "plus" | "default"
 */
export const Counter = ({
  value,
  duration = 1800,
  prefix = "",
  suffix = "",
  format = "default",
  className = "",
  testid,
}) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const animate = (t) => {
              const p = Math.min((t - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setN(Math.floor(eased * value));
              if (p < 1) requestAnimationFrame(animate);
              else setN(value);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  const formatted = (() => {
    if (format === "currency-brl") {
      return `R$ ${n.toLocaleString("pt-BR")}`;
    }
    if (format === "plus") {
      return `${n}+`;
    }
    return n.toLocaleString("pt-BR");
  })();

  return (
    <span
      ref={ref}
      className={className}
      data-testid={testid}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default Counter;
